import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AuthorizationService, AccountsService } from "@/lib/api_client";
import type { UserProfile, UserRole } from "@/lib/api_client";
import { fetchAll } from "./fetchAll";
import { getAuthHeaders } from "@/lib/api";

export const useUserProfiles = () =>
  useQuery({
    queryKey: ["user-profiles"],
    queryFn: () => fetchAll<UserProfile>(AccountsService.getApiUserProfiles),
    staleTime: 5 * 60_000,
  });

export const useUserRoles = (userId: string | null) =>
  useQuery({
    queryKey: ["authorization", "roles", userId],
    queryFn: async () => {
      const res = await AuthorizationService.getApiAuthorizationUserRoles({
        query: { "filter[authUserId]": userId! },
        headers: getAuthHeaders(),
      });
      return (res.data ?? []) as UserRole[];
    },
    enabled: !!userId,
  });

export const useGrantRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      authUserId: string;
      role: string;
      grantReason: string;
      organization?: string;
      expiresAt?: string;
    }) => {
      const res = await AuthorizationService.postApiAuthorizationUserRoles({
        body: {
          authUserId: params.authUserId,
          role: params.role,
          grantReason: params.grantReason,
          ...(params.organization ? { organization: params.organization } : {}),
          ...(params.expiresAt ? { expiresAt: params.expiresAt } : {}),
        },
        headers: getAuthHeaders(),
      });
      if (res.error) throw new Error((res.error as any)?.error ?? "Failed to grant role");
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["authorization", "roles", vars.authUserId] });
    },
  });
};

export const useRevokeRole = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { roleId: string; authUserId: string }) => {
      // Revoking is a status change; the policy refuses DELETE so the row stays for the audit trail.
      const res = await AuthorizationService.putApiAuthorizationUserRolesById({
        path: { id: params.roleId },
        body: { status: "revoked" },
        headers: getAuthHeaders(),
      });
      if (res.error) throw new Error((res.error as any)?.error ?? "Failed to revoke role");
      return res.data;
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ["authorization", "roles", vars.authUserId] });
    },
  });
};

export const useStaffAtDailyPatientLimit = () =>
  useQuery({
    queryKey: ["authorization", "daily-patient-limit"],
    queryFn: async () => {
      const res = await AuthorizationService.getApiAuthorizationDailyPatientLimit({
        headers: getAuthHeaders(),
      });
      if (res.error) throw new Error((res.error as any)?.error ?? "Failed to load daily patient limits");
      return res.data ?? [];
    },
  });

export const useResetDailyPatientLimit = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: { staffUserId: string; reason: string }) => {
      const res = await AuthorizationService.postApiAuthorizationDailyPatientLimitReset({
        body: params,
        headers: getAuthHeaders(),
      });
      if (res.error) throw new Error((res.error as any)?.error ?? "Failed to reset daily patient limit");
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["authorization", "daily-patient-limit"] });
    },
  });
};
